import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsCashCoin, BsPlusCircle, BsShieldCheck } from 'react-icons/bs';
import Loader from '../../components/shared/Loader';
import { GridExportButton, GridSearchInput } from '../../components/shared/GridToolbar';
import {
  LIST_CONSOLE_AG_GRID_CLASS,
  ListConsoleGridSection,
  ListConsolePage,
} from '../../components/shared/ListConsoleLayout';
import '../transaction-monitoring/TransactionMonitoring.css';
import { ActiveGridFiltersBar } from '../../components/shared/agGridCells';
import {
  GridColumnFilterButton,
  GridColumnFilterPanel,
  formatDateRangeFilterLabel,
} from '../../components/shared/GridColumnFilterPanel';
import { type ActiveGridFilter } from '../../utils/gridColumnFilters';
import SearchableSelect, { type SearchableSelectOption } from '../../components/shared/SearchableSelect';
import DeleteConfirmDialog, { type ConfirmDetailRow } from '../../components/shared/DeleteConfirmDialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { DataGrid, type GridApi } from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { showToast } from '../../store/toastStore';
import { withExcelExportFeedback } from '../../utils/backendExport';
import {
  addDccMarkupRate,
  deactivateDccMarkupRate,
  fetchActiveMerchants,
  fetchDccMarkupRates,
  fetchMerchantTerminals,
  fetchTerminalDccStatus,
  setTerminalDccStatus,
} from './api/api';
import { EMPTY_DCC_MARKUP_RATE_COLUMN_FILTERS, EMPTY_DCC_RATE_FORM } from './constants/constants';
import type {
  DccMarkupRateColumnFilterKey,
  DccMarkupRateColumnFilters,
  DccMarkupRateGridContext,
  DccMarkupRateGridRow,
  DccMarkupRateRow,
} from './models/types';
import {
  buildDccMarkupRateColumnFilterFields,
  createDccMarkupRateColumnDefs,
  DCC_MARKUP_RATE_COLUMN_LABELS,
} from './table/columnDefs';
import {
  DCC_MARKUP_RATE_GRID_DEFAULT_COL_DEF,
  dccMarkupRateGridOptions,
  dccMarkupRateSearchEmptyGridOptions,
  GRID_PAGE_SIZE,
  GRID_PAGE_SIZE_SELECTOR,
} from './table/gridOptions';
import {
  buildDccMarkupRateGridRows,
  exportDccMarkupRatesExcel,
  getApiErrorMessage,
  mapAgGridFilterModelToDccMarkupRateFilters,
  rowMatchesDccColumnFilters,
} from './utils/utils';
import './DccMarkupRates.css';

const DccMarkupRates: React.FC = () => {
  const [merchantOptions, setMerchantOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState('');

  const [terminalOptions, setTerminalOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingTerminals, setLoadingTerminals] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState('');

  const [rates, setRates] = useState<DccMarkupRateRow[]>([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [ratesError, setRatesError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [columnFilters, setColumnFilters] = useState<DccMarkupRateColumnFilters>(
    EMPTY_DCC_MARKUP_RATE_COLUMN_FILTERS,
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [gridFilterEpoch, setGridFilterEpoch] = useState(0);
  const gridApiRef = useRef<GridApi<DccMarkupRateGridRow> | null>(null);

  const [showRateModal, setShowRateModal] = useState(false);
  const [rateForm, setRateForm] = useState(EMPTY_DCC_RATE_FORM);
  const [savingRate, setSavingRate] = useState(false);
  const [rateFormError, setRateFormError] = useState<string | null>(null);

  const [deactivateTarget, setDeactivateTarget] = useState<DccMarkupRateRow | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const [dccEnabled, setDccEnabled] = useState<boolean | null>(null);
  const [loadingDccStatus, setLoadingDccStatus] = useState(false);
  const [dccStatusError, setDccStatusError] = useState<string | null>(null);
  const [togglingDcc, setTogglingDcc] = useState(false);

  const loadMerchants = useCallback(async () => {
    setLoadingMerchants(true);
    try {
      setMerchantOptions(await fetchActiveMerchants());
    } catch (e) {
      console.error('Failed to fetch merchants:', e);
    } finally {
      setLoadingMerchants(false);
    }
  }, []);

  const loadTerminals = useCallback(async (merchantId: number) => {
    if (!merchantId) {
      setTerminalOptions([]);
      return;
    }
    setLoadingTerminals(true);
    try {
      setTerminalOptions(await fetchMerchantTerminals(merchantId));
    } catch (e) {
      console.error('Failed to fetch merchant terminals:', e);
      setTerminalOptions([]);
    } finally {
      setLoadingTerminals(false);
    }
  }, []);

  const loadRates = useCallback(async (terminalId: number) => {
    if (!terminalId) {
      setRates([]);
      return;
    }
    setLoadingRates(true);
    setRatesError(null);
    try {
      setRates(await fetchDccMarkupRates(terminalId));
    } catch (e) {
      console.error('Failed to fetch DCC markup rates:', e);
      setRatesError(getApiErrorMessage(e, 'Could not load DCC markup rates. Please try again.'));
    } finally {
      setLoadingRates(false);
    }
  }, []);

  const loadDccStatus = useCallback(async (terminalId: number) => {
    if (!terminalId) {
      setDccEnabled(null);
      setDccStatusError(null);
      return;
    }
    setLoadingDccStatus(true);
    setDccStatusError(null);
    try {
      setDccEnabled(await fetchTerminalDccStatus(terminalId));
    } catch (e) {
      console.error('Failed to fetch DCC status:', e);
      setDccEnabled(null);
      setDccStatusError(getApiErrorMessage(e, 'Failed to load DCC status for this terminal.'));
    } finally {
      setLoadingDccStatus(false);
    }
  }, []);

  useEffect(() => {
    loadMerchants();
  }, [loadMerchants]);

  const resetColumnFilters = useCallback(() => {
    setColumnFilters(EMPTY_DCC_MARKUP_RATE_COLUMN_FILTERS);
    const api = gridApiRef.current;
    if (api && Object.keys(api.getFilterModel() ?? {}).length > 0) {
      api.setFilterModel(null);
      api.onFilterChanged();
    }
  }, []);

  const handleMerchantChange = (value: string) => {
    setSelectedMerchant(value);
    setSelectedTerminal('');
    setRates([]);
    setDccEnabled(null);
    resetColumnFilters();
    const merchantId = Number(value);
    if (merchantId) loadTerminals(merchantId);
    else setTerminalOptions([]);
  };

  const handleTerminalChange = (value: string) => {
    setSelectedTerminal(value);
    resetColumnFilters();
    const terminalId = Number(value);
    if (terminalId) {
      loadRates(terminalId);
      loadDccStatus(terminalId);
    } else {
      setRates([]);
      setDccEnabled(null);
    }
  };

  const handleToggleDcc = async (checked: boolean) => {
    const terminalId = Number(selectedTerminal);
    if (!terminalId || dccEnabled === null || togglingDcc) return;
    setTogglingDcc(true);
    try {
      const res = await setTerminalDccStatus(terminalId, checked);
      setDccEnabled(!!res?.data?.dccEnabled || checked);
      showToast(
        checked ? 'DCC enabled for this terminal.' : 'DCC disabled for this terminal.',
        'success',
      );
    } catch (err) {
      console.error('Failed to update DCC status:', err);
      showToast(getApiErrorMessage(err, 'Failed to update DCC status.'), 'error');
    } finally {
      setTogglingDcc(false);
    }
  };

  const merchantSelectOptions = useMemo(
    () => merchantOptions.map((m) => ({ value: String(m.id), label: m.name })),
    [merchantOptions],
  );

  const terminalSelectOptions = useMemo(
    () => terminalOptions.map((t) => ({ value: String(t.id), label: t.name })),
    [terminalOptions],
  );

  const merchantPlaceholder = loadingMerchants ? 'Loading merchants…' : 'Select merchant';

  const terminalPlaceholder = !selectedMerchant
    ? 'Select a merchant first'
    : loadingTerminals
      ? 'Loading terminals…'
      : 'Select merchant terminal';

  const renderMerchantValue = useCallback(
    (option: SearchableSelectOption | undefined) => option?.label ?? merchantPlaceholder,
    [merchantPlaceholder],
  );

  const renderTerminalValue = useCallback(
    (option: SearchableSelectOption | undefined) => option?.label ?? terminalPlaceholder,
    [terminalPlaceholder],
  );

  const handleRemoveColumnFilter = useCallback((key: string) => {
    setColumnFilters((prev) => ({
      ...prev,
      [key]: null,
    }));
    const api = gridApiRef.current;
    if (api?.getColumnFilterModel(key)) {
      void api.setColumnFilterModel(key, null).then(() => {
        api.onFilterChanged();
      });
    }
  }, []);

  const handleClearColumnFilters = useCallback(() => {
    setColumnFilters(EMPTY_DCC_MARKUP_RATE_COLUMN_FILTERS);
    const api = gridApiRef.current;
    if (api && Object.keys(api.getFilterModel() ?? {}).length > 0) {
      api.setFilterModel(null);
      api.onFilterChanged();
    }
  }, []);

  const handleApplyColumnFilters = useCallback((values: Record<string, string[] | null>) => {
    gridApiRef.current?.setFilterModel(null);
    setColumnFilters({
      ...EMPTY_DCC_MARKUP_RATE_COLUMN_FILTERS,
      ...(Object.fromEntries(
        (Object.keys(EMPTY_DCC_MARKUP_RATE_COLUMN_FILTERS) as DccMarkupRateColumnFilterKey[]).map(
          (key) => [key, values[key]?.length ? values[key] : null],
        ),
      ) as DccMarkupRateColumnFilters),
    });
  }, []);

  const handleGridFilterChanged = useCallback(() => {
    setGridFilterEpoch((n) => n + 1);
  }, []);

  const handleGridReady = useCallback((event: { api: GridApi<DccMarkupRateGridRow> }) => {
    gridApiRef.current = event.api;
  }, []);

  const mergedColumnFilters = useMemo<DccMarkupRateColumnFilters>(() => {
    void gridFilterEpoch;
    const nativeColumnFilters = mapAgGridFilterModelToDccMarkupRateFilters(
      gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
    );
    return {
      ...columnFilters,
      ...nativeColumnFilters,
    };
  }, [columnFilters, gridFilterEpoch]);

  const activeColumnFilters = useMemo<ActiveGridFilter[]>(() => {
    return (Object.entries(mergedColumnFilters) as [DccMarkupRateColumnFilterKey, string[] | null][])
      .filter(([, value]) => Boolean(value?.length))
      .map(([key, value]) => ({
        key,
        label: DCC_MARKUP_RATE_COLUMN_LABELS[key],
        value:
          key === 'createdAt'
            ? formatDateRangeFilterLabel(value)
            : (value as string[]).join(', '),
      }));
  }, [mergedColumnFilters]);

  const columnFilterFields = useMemo(() => {
    const fields = buildDccMarkupRateColumnFilterFields(rates);
    return fields.map((field) => {
      if (field.type === 'dateRange') return field;
      const applied = mergedColumnFilters[field.key as DccMarkupRateColumnFilterKey];
      if (!applied?.length) return field;
      const missing = applied.filter((value) => !field.options.includes(value));
      if (!missing.length) return field;
      return {
        ...field,
        options: [...field.options, ...missing],
      };
    });
  }, [rates, mergedColumnFilters]);

  const rateGridRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return buildDccMarkupRateGridRows(rates).filter((row) => {
      if (q) {
        const matchesSearch = [
          row.fromCurrency,
          row.toCurrency,
          row.pair,
          row.rateLabel,
          row.status,
          row.notes ?? '',
          String(row.rate),
        ].some((field) => field.toLowerCase().includes(q));
        if (!matchesSearch) return false;
      }
      return rowMatchesDccColumnFilters(row, mergedColumnFilters);
    });
  }, [rates, search, mergedColumnFilters]);

  const rateColumnDefs = useMemo(() => createDccMarkupRateColumnDefs(), []);

  const handleExport = async () => {
    if (rateGridRows.length === 0) return;
    setExporting(true);
    try {
      await withExcelExportFeedback(
        () => exportDccMarkupRatesExcel(rateGridRows),
        { error: 'Failed to export DCC markup rates.' },
      );
    } finally {
      setExporting(false);
    }
  };

  const rateGridOptions = useMemo(
    () => ({
      ...(rates.length > 0 && rateGridRows.length === 0
        ? dccMarkupRateSearchEmptyGridOptions
        : dccMarkupRateGridOptions),
      onFilterChanged: handleGridFilterChanged,
    }),
    [handleGridFilterChanged, rateGridRows.length, rates.length],
  );

  useEffect(() => {
    gridApiRef.current?.paginationGoToFirstPage();
  }, [search, columnFilters, gridFilterEpoch, selectedTerminal]);

  const handleDeactivateClick = useCallback((rate: DccMarkupRateRow) => {
    setDeactivateError(null);
    setDeactivateTarget(rate);
  }, []);

  const rateGridContext = useMemo<DccMarkupRateGridContext>(
    () => ({ onDeactivate: handleDeactivateClick }),
    [handleDeactivateClick],
  );

  const openRateModal = () => {
    setRateForm(EMPTY_DCC_RATE_FORM);
    setRateFormError(null);
    setShowRateModal(true);
  };

  const closeRateModal = () => {
    if (savingRate) return;
    setShowRateModal(false);
    setRateForm(EMPTY_DCC_RATE_FORM);
    setRateFormError(null);
  };

  const handleRateFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setRateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRateFormError(null);

    const terminalId = Number(selectedTerminal);
    const fromCurrency = rateForm.fromCurrency.trim().toUpperCase();
    const toCurrency = rateForm.toCurrency.trim().toUpperCase();
    const rateValue = Number(rateForm.rate);

    if (!terminalId) {
      setRateFormError('Select a merchant terminal first.');
      return;
    }
    if (fromCurrency.length !== 3 || toCurrency.length !== 3) {
      setRateFormError('Currency codes must be 3 letters, e.g. EUR, USD.');
      return;
    }
    // Same currency is allowed on purpose: a USD -> USD rate of 1.01 is a +1% markup (no currency
    // conversion). The marked-up amount is what reaches the acquirer; the merchant still sees the
    // original amount and only a super admin sees the amount sent to the acquirer.
    if (!rateValue || rateValue <= 0) {
      setRateFormError('Enter a rate greater than zero.');
      return;
    }

    setSavingRate(true);
    try {
      await addDccMarkupRate({
        merchantTerminalId: terminalId,
        fromCurrency,
        toCurrency,
        rate: rateValue,
        notes: rateForm.notes.trim() || null,
      });
      showToast('DCC markup rate saved.', 'success');
      setShowRateModal(false);
      setRateForm(EMPTY_DCC_RATE_FORM);
      await loadRates(terminalId);
    } catch (err) {
      console.error('Failed to save DCC markup rate:', err);
      setRateFormError(getApiErrorMessage(err, 'Failed to save DCC markup rate.'));
    } finally {
      setSavingRate(false);
    }
  };

  const closeDeactivateDialog = () => {
    if (deactivating) return;
    setDeactivateTarget(null);
    setDeactivateError(null);
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    setDeactivateError(null);
    try {
      await deactivateDccMarkupRate(deactivateTarget.id);
      showToast(`Rate ${deactivateTarget.fromCurrency} → ${deactivateTarget.toCurrency} deactivated.`, 'success');
      setDeactivateTarget(null);
      await loadRates(Number(selectedTerminal));
    } catch (err) {
      console.error('Failed to deactivate DCC markup rate:', err);
      const message = getApiErrorMessage(err, 'Failed to deactivate rate.');
      setDeactivateError(message);
      showToast(message, 'error');
    } finally {
      setDeactivating(false);
    }
  };

  const deactivateDetails: ConfirmDetailRow[] = deactivateTarget
    ? [
        { label: 'Terminal', value: deactivateTarget.merchantTerminalName },
        { label: 'Pair', value: `${deactivateTarget.fromCurrency} → ${deactivateTarget.toCurrency}` },
        { label: 'Rate', value: `1 ${deactivateTarget.fromCurrency} = ${deactivateTarget.rate} ${deactivateTarget.toCurrency}` },
      ]
    : [];

  const dccStatusPanelClass = dccStatusError
    ? 'dcc-status-panel is-error'
    : dccEnabled
      ? 'dcc-status-panel is-enabled'
      : 'dcc-status-panel';

  return (
    <ListConsolePage
      title="DCC Markup Rates"
      actions={
        <Button
          type="button"
          size="sm"
          className="tm-console-action-btn gap-1.5"
          onClick={openRateModal}
          disabled={!selectedTerminal}
        >
          <BsPlusCircle className="h-3 w-3 shrink-0" aria-hidden />
          Add Rate
        </Button>
      }
    >
      <ListConsoleGridSection
        toolbarLeft={
          <div className="dcc-filter-row flex flex-wrap items-center gap-2">
            <div className="dcc-filter-select">
              <SearchableSelect
                className="h-9"
                value={selectedMerchant}
                options={merchantSelectOptions}
                onChange={handleMerchantChange}
                placeholder={merchantPlaceholder}
                renderValue={renderMerchantValue}
                searchPlaceholder="Search merchant…"
                emptyMessage="No merchants available"
                disabled={loadingMerchants}
                aria-label="Merchant"
              />
            </div>
            <div className="dcc-filter-select">
              <SearchableSelect
                className="h-9"
                value={selectedTerminal}
                options={terminalSelectOptions}
                onChange={handleTerminalChange}
                placeholder={terminalPlaceholder}
                renderValue={renderTerminalValue}
                searchPlaceholder="Search terminal…"
                emptyMessage={!selectedMerchant ? 'Select a merchant first' : 'No terminals available'}
                disabled={!selectedMerchant || loadingTerminals}
                aria-label="Merchant terminal"
              />
            </div>
          </div>
        }
        toolbarRight={
          <div className="tm-toolbar-controls">
            <GridSearchInput
              value={search}
              onValueChange={setSearch}
              placeholder="Search currency, status, notes…"
              className="tm-toolbar-search"
              disabled={!selectedTerminal}
              aria-label="Search rates"
            />
            <GridColumnFilterButton
              activeCount={activeColumnFilters.length}
              onClick={() => setFilterPanelOpen(true)}
              disabled={!selectedTerminal || loadingRates}
              className="tm-compact-icon-btn"
            />
            <GridExportButton
              onClick={() => void handleExport()}
              loading={exporting}
              disabled={!selectedTerminal || loadingRates || rateGridRows.length === 0}
              className="tm-compact-icon-btn"
            />
          </div>
        }
        between={
          selectedTerminal ? (
        <div className={dccStatusPanelClass}>
          {loadingDccStatus ? (
            <div className="dcc-status-loading">
              <Loader size="sm" />
              <span className="dcc-status-sub">Loading DCC status…</span>
            </div>
          ) : dccStatusError ? (
            <>
              <div>
                <div className="dcc-status-title is-error">Couldn&apos;t load DCC status</div>
                <div className="dcc-status-sub">{dccStatusError}</div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => loadDccStatus(Number(selectedTerminal))}
              >
                Retry
              </Button>
            </>
          ) : (
            <>
              <div>
                <div className="dcc-status-title">
                  DCC conversion is {dccEnabled ? 'enabled' : 'disabled'} on this terminal
                </div>
                <div className="dcc-status-sub">
                  {dccEnabled
                    ? 'Active rates below are applied to matching transactions.'
                    : 'All rates below are paused no conversion happens, even if rates are ACTIVE. Turn this back on to resume without re-entering rates.'}
                </div>
              </div>
              <Switch
                checked={!!dccEnabled}
                onCheckedChange={handleToggleDcc}
                disabled={togglingDcc}
                aria-label="Toggle DCC conversion"
              />
            </>
          )}
        </div>
          ) : null
        }
        filterBar={
          selectedTerminal && !ratesError ? (
            <ActiveGridFiltersBar
              filters={activeColumnFilters}
              onRemove={handleRemoveColumnFilter}
              onClearAll={handleClearColumnFilters}
            />
          ) : null
        }
      >
        {!selectedTerminal ? (
          <div className="dcc-empty-card flex min-h-[12rem] items-center justify-center p-8 text-center text-sm text-muted-foreground">
            Select a merchant and terminal above to view or add DCC markup rates.
          </div>
        ) : ratesError ? (
          <div className="m-4 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span>{ratesError}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit shrink-0"
                onClick={() => loadRates(Number(selectedTerminal))}
              >
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <DataGrid<DccMarkupRateGridRow>
            rowData={rateGridRows}
            columnDefs={rateColumnDefs}
            context={rateGridContext}
            includeAuditColumns={false}
            loading={loadingRates}
            pagination
            paginationPageSize={GRID_PAGE_SIZE}
            paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
            height="100%"
            className={LIST_CONSOLE_AG_GRID_CLASS}
            onGridReady={handleGridReady}
            defaultColDef={DCC_MARKUP_RATE_GRID_DEFAULT_COL_DEF}
            gridOptions={rateGridOptions}
          />
        )}
      </ListConsoleGridSection>

      <Dialog open={showRateModal} onOpenChange={(open) => !open && closeRateModal()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Add DCC Markup Rate</DialogTitle>
            <DialogDescription>
              Configure a currency conversion rate for the selected merchant terminal.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRateSubmit} className="space-y-4 mt-2">
            <div className="grid gap-5 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="dcc-from-currency" className="flex items-center gap-2">
                  <BsCashCoin className="text-accent" />
                  From Currency (merchant submits)
                </Label>
                <Input
                  id="dcc-from-currency"
                  name="fromCurrency"
                  value={rateForm.fromCurrency}
                  onChange={handleRateFormChange}
                  placeholder="EUR"
                  maxLength={3}
                  className="uppercase"
                  disabled={savingRate}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dcc-to-currency" className="flex items-center gap-2">
                  <BsShieldCheck className="text-accent" />
                  To Currency (terminal settles)
                </Label>
                <Input
                  id="dcc-to-currency"
                  name="toCurrency"
                  value={rateForm.toCurrency}
                  onChange={handleRateFormChange}
                  placeholder="USD"
                  maxLength={3}
                  className="uppercase"
                  disabled={savingRate}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dcc-rate" className="flex items-center gap-2">Rate (1 From = ? To)</Label>
                <Input
                  id="dcc-rate"
                  name="rate"
                  type="number"
                  step="0.00000001"
                  min="0"
                  value={rateForm.rate}
                  onChange={handleRateFormChange}
                  placeholder="1.10"
                  disabled={savingRate}
                />
              </div>
              <div className="space-y-2 sm:col-span-3">
                <Label htmlFor="dcc-notes">Notes (optional, admin-only)</Label>
                <Textarea
                  id="dcc-notes"
                  name="notes"
                  value={rateForm.notes}
                  onChange={handleRateFormChange}
                  placeholder="e.g. Agreed with merchant on 2026-07-01"
                  rows={2}
                  disabled={savingRate}
                />
              </div>
            </div>

            <Alert>
              <AlertDescription>
                Saving this deactivates any currently active rate for this terminal and currency
                pair and inserts this as the new version old rates are kept for audit, never
                deleted.
              </AlertDescription>
            </Alert>

            {rateFormError && (
              <Alert variant="destructive">
                <AlertDescription>{rateFormError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeRateModal} disabled={savingRate}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingRate}>
                {savingRate ? 'Saving…' : 'Save Rate'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate DCC Markup Rate"
        entityLabel="Rate"
        entityName={deactivateTarget ? `${deactivateTarget.fromCurrency} → ${deactivateTarget.toCurrency}` : ''}
        description="Deactivating stops this rate from applying to new transactions. The rate history is kept for audit."
        details={deactivateDetails}
        confirmLabel="Deactivate"
        loading={deactivating}
        error={deactivateError}
        onConfirm={confirmDeactivate}
        onCancel={closeDeactivateDialog}
      />

      <GridColumnFilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        fields={columnFilterFields}
        values={mergedColumnFilters}
        multiple
        onApply={handleApplyColumnFilters}
        onClear={handleClearColumnFilters}
      />
    </ListConsolePage>
  );
};

export default DccMarkupRates;
